import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";
import { RecordLinkRow } from "@/components/crm/RecordLinkRow";

type LeadRow = {
  id: string;
  leadName?: string;
  companyName?: string | null;
  status?: string;
  updatedAt?: string;
};

export function CrmLeadsConvertedPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=500");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load leads.");
        setLeads([]);
      } else {
        setLeads((res.data?.leads ?? []).filter((l) => l.status === "converted"));
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Converted leads</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">Leads that finished conversion to accounts and contacts.</p>
        </div>
        <Link
          to="/crm/leads"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Open leads
        </Link>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      {!loading && !error ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
              <tr>
                <th className="px-4 py-3 font-semibold">Lead</th>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <RecordLinkRow key={lead.id} href={`/crm/leads/${encodeURIComponent(lead.id)}`}>
                  <td className="px-4 py-3 font-medium">{lead.leadName || lead.id}</td>
                  <td className="px-4 py-3">{lead.companyName || "—"}</td>
                  <td className="px-4 py-3">{formatCrmStatusLabel(lead.status)}</td>
                  <td className="px-4 py-3">{lead.updatedAt ? new Date(lead.updatedAt).toLocaleString() : "—"}</td>
                </RecordLinkRow>
              ))}
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                    No converted leads yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
